import { ServiceProviderError } from '@takeshape/errors';
import { isGraphqlResolver } from '@takeshape/schema';
import { value } from '@takeshape/util';
import set from 'lodash/set.js';
import { isOAuth2TokenResponse } from "../types.js";
import { updateAuthentication } from "../util/authentication.js";
import { hasEveryKey } from "../util/has.js";
import { getHealthCheck } from "../util/health.js";
import { apiVersionFormConfig, apiVersionSchema, getInitialData, getStorefrontUrl, parseShopifyHostName } from "./shopify.js";
export const DELEGATE_TOKEN_TTL = 24 * 60 * 60; // 24hrs
export const DELEGATE_TOKEN_SCOPES = [
    'unauthenticated_write_checkouts',
    'unauthenticated_read_checkouts',
    'unauthenticated_write_customers',
    'unauthenticated_read_customers',
    'unauthenticated_read_product_listings',
    'unauthenticated_read_selling_plans',
    'unauthenticated_read_product_inventory',
    'unauthenticated_read_product_tags',
    'unauthenticated_read_content',
    'unauthenticated_read_customer_tags',
    'unauthenticated_read_metaobjects',
    'unauthenticated_read_product_pickup_locations',
    'unauthenticated_write_bulk_operations',
    'unauthenticated_read_bulk_operations',
    'unauthenticated_write_gates',
    'unauthenticated_read_gates'
];
const providerId = 'shopifyStorefront';
const title = 'Shopify Storefront';
const namespace = 'ShopifyStorefront';
export function addBuyerIpHeaderConfig(layer) {
    for (const prop of ['queries', 'mutations']) {
        for (const query of Object.values(layer[prop])) {
            if (isGraphqlResolver(query.resolver)) {
                query.resolver.headers = {
                    ops: [
                        {
                            path: 'Shopify-Storefront-Buyer-IP',
                            mapping: '$request.ipAddress'
                        }
                    ]
                };
            }
        }
    }
    return layer;
}
export function createDelegateTokenRequest(serviceConfig) {
    return {
        url: `https://${serviceConfig.options.shop}.myshopify.com/admin/access_tokens/delegate.json`,
        headers: {
            'X-Shopify-Access-Token': serviceConfig.authentication.adminApiAccessToken
        },
        method: 'POST',
        json: {
            delegate_access_scope: DELEGATE_TOKEN_SCOPES,
            expires_in: DELEGATE_TOKEN_TTL
        }
    };
}
const formSchema = {
    type: 'object',
    properties: {
        id: {
            title: 'Slug',
            type: 'string',
            pattern: '[0-9a-z_-]+',
            default: 'shopify-storefront'
        },
        title: {
            title: 'Name',
            type: 'string',
            default: title
        },
        namespace: {
            title: 'Namespace',
            type: 'string',
            pattern: '^[A-Z][A-Za-z]+$',
            default: namespace
        },
        shop: {
            title: '.myshopify.com URL',
            type: 'string'
        },
        apiVersion: apiVersionSchema,
        authentication: {
            title: 'Authentication',
            type: 'object',
            properties: {
                adminApiAccessToken: {
                    title: 'Admin API Access Token',
                    type: 'string'
                }
            },
            required: ['adminApiAccessToken']
        }
    },
    required: ['id', 'title', 'namespace', 'shop', 'apiVersion', 'authentication']
};
const serviceProvider = {
    name: providerId,
    title,
    description: 'Shopify is a commerce platform that allows anyone to easily sell online, at a retail location, and everywhere in between.',
    categories: ['ecommerce'],
    links: [
        { title: 'Homepage', url: 'https://www.shopify.com/' },
        { title: 'Docs', url: '/docs/services/providers/shopify' }
    ],
    serviceType: 'graphql',
    introspectionType: 'graphql',
    createId: value('shopify-storefront'),
    getFormSchema: value(formSchema),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'shop', 'apiVersion', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'The human-readable unique identifier for this service.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A pascal-case prefix that will be added to types in your GraphQL API.'
            },
            shop: {
                widget: 'singleLineText',
                instructions: 'ex: https://example-shop.myshopify.com from Shopify'
            },
            apiVersion: apiVersionFormConfig,
            authentication: {
                order: ['adminApiAccessToken'],
                properties: {
                    adminApiAccessToken: {
                        widget: 'sensitiveField'
                    }
                }
            }
        }
    }),
    getInitialData,
    getConfig(data) {
        const formData = data;
        const config = {
            title: formData.title ?? title,
            namespace: formData.namespace ?? namespace,
            provider: providerId,
            serviceType: 'graphql',
            authenticationType: 'custom'
        };
        if (formData.apiVersion) {
            set(config, 'options.apiVersion', formData.apiVersion);
        }
        if (formData.shop) {
            set(config, 'options.shop', parseShopifyHostName(formData.shop));
            set(config, 'options.endpoint', getStorefrontUrl(parseShopifyHostName(formData.shop), formData.apiVersion));
        }
        return updateAuthentication(config, formData, { type: 'custom' });
    },
    getAuthInfoCacheExpiration: value(DELEGATE_TOKEN_TTL),
    getAuthInfoRequestConfig: createDelegateTokenRequest,
    getAuthInfoResult(_, response) {
        if (!isOAuth2TokenResponse(response)) {
            throw new ServiceProviderError(`${title} - invalid auth response`);
        }
        return [[['headers', 'Shopify-Storefront-Private-Token'], response.access_token]];
    },
    isCompleteConfig: hasEveryKey(['authentication', 'options.shop', 'options.endpoint']),
    getHealthCheck: getHealthCheck({ checkName: 'graphqlIntrospection' }),
    schemaTransform: addBuyerIpHeaderConfig
};
export default serviceProvider;
